/**
 * internalProjects — canonieke definitie van de zes interne projecten en de vraag
 * of hun uren als gewerkte uren tellen.
 *
 * Eén bron van waarheid voor drie consumenten: de modulepagina
 * `pages/admin/settings/modules/urenregistratie.vue` (die de rijen tekent en projecten
 * aanmaakt), de rekenlogica in `components/blocks/WeekSummary.vue` en
 * `components/blocks/UrenoverzichtTable.vue`, en de superadmin-flow
 * `pages/beheer/create-company.vue`.
 *
 * Een intern project bestaat pas voor een bedrijf zodra de bijbehorende
 * `company.project_id_*`-pointer gevuld is; tot die tijd is de titel hieronder
 * alleen de standaardnaam die de pagina voorstelt. Elke rij heeft een eigen
 * `countsSetting`: een schakelaar op `company` die bepaalt of die uren meetellen.
 */

/**
 * @typedef {Object} InternalProjectDef
 * @property {string} field - pointerveld op de `company`-record
 * @property {string} title - standaardnaam, gebruikt tot het project bestaat
 * @property {string} countsSetting - boolean-veld op `company` dat bepaalt of deze
 *   uren als werkuren tellen (weektotaal en overuren).
 * @property {boolean} countsDefault - wat geldt als het veld `null`/ontbreekt. Reisuren
 *   telden historisch mee; de andere vijf zijn opt-in en tellen standaard niet mee.
 */

/** @type {InternalProjectDef[]} */
export const INTERNAL_PROJECTS = [
  { field: 'project_id_vakantie', title: 'Vakantie/ATV (vrij)', countsSetting: 'vacation_hours_count_as_work', countsDefault: false },
  { field: 'project_id_verlof', title: 'Verlof (dokter, tandarts, etc.)', countsSetting: 'leave_hours_count_as_work', countsDefault: false },
  { field: 'project_id_ziekte', title: 'Ziekte', countsSetting: 'sick_hours_count_as_work', countsDefault: false },
  { field: 'project_id_tvt', title: 'Verlof (tijd voor tijd)', countsSetting: 'tvt_hours_count_as_work', countsDefault: false },
  { field: 'project_id_feestdag', title: 'Feestdag (officiële vrije dag)', countsSetting: 'holiday_hours_count_as_work', countsDefault: false },
  { field: 'project_id_reisuren', title: 'Reisuren', countsSetting: 'travel_hours_count_as_work', countsDefault: true },
];

/**
 * Ids van de interne projecten waarvan de uren niet als gewerkte uren tellen.
 *
 * Vervangt twee oudere mechanismen: de titelmatch op 'Reisuren' in WeekSummary en de
 * hardgecodeerde uitsluitingslijst in UrenoverzichtTable.
 *
 * Per rij beslist `company[countsSetting]`; `null`/ontbrekend valt terug op
 * `countsDefault`. Directus levert op MySQL soms 0/1 — `Boolean()` vangt dat.
 *
 * @param {Object|null|undefined} company - de company-record uit de store
 * @returns {string[]} project-ids, zonder lege pointers
 */
export function nonWorkingProjectIds(company) {
  return INTERNAL_PROJECTS.filter((def) => !Boolean(company?.[def.countsSetting] ?? def.countsDefault))
    .map((def) => company?.[def.field])
    .filter(Boolean);
}
